/**
 * Who is signed in, and which of the three faces to show them.
 *
 * `/auth/me` returns the shape this asks for, so the client never infers a role
 * from anything it stores locally — the server is the only thing that decides
 * what someone is allowed to be.
 */
import type { AuthenticatedUser } from '@bjsfabric/shared'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { api, setAccessToken, setSessionLostHandler } from '../api/client'

interface AuthResponse {
  accessToken: string
  expiresInSeconds: number
  user: AuthenticatedUser
}

interface SessionValue {
  /** `loading` only until the opening refresh has answered. */
  status: 'loading' | 'signedIn' | 'signedOut'
  user: AuthenticatedUser | null
  signIn: (email: string, password: string) => Promise<AuthenticatedUser>
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionValue['status']>('loading')
  const [user, setUser] = useState<AuthenticatedUser | null>(null)

  const forgetSession = useCallback(() => {
    setAccessToken(null)
    setUser(null)
    setStatus('signedOut')
  }, [])

  useEffect(() => {
    // Any request that finally fails with 401 lands here, so an expired or
    // revoked session moves the whole app to the sign-in screen rather than
    // leaving one panel showing an error.
    setSessionLostHandler(forgetSession)
  }, [forgetSession])

  useEffect(() => {
    let cancelled = false

    // The opening question: is the refresh cookie still good? The access token
    // is deliberately not persisted, so this is the only way to know.
    void (async () => {
      const recovered = await api.refresh()
      if (cancelled) return
      if (!recovered) {
        setStatus('signedOut')
        return
      }
      try {
        const { user: me } = await api.get<{ user: AuthenticatedUser }>('/auth/me')
        if (cancelled) return
        setUser(me)
        setStatus('signedIn')
      } catch {
        if (!cancelled) forgetSession()
      }
    })()

    return () => {
      cancelled = true
    }
  }, [forgetSession])

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.post<AuthResponse>(
      '/auth/login',
      { email, password },
      // A failed sign-in is a 401 that must not be read as "your session
      // ended" — there is no session yet.
      { anonymous: true },
    )
    setAccessToken(result.accessToken)
    setUser(result.user)
    setStatus('signedIn')
    return result.user
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      // Locally signed out even if the request failed: the alternative is a
      // button that appears not to work.
      forgetSession()
    }
  }, [forgetSession])

  const refreshUser = useCallback(async () => {
    const { user: me } = await api.get<{ user: AuthenticatedUser }>('/auth/me')
    setUser(me)
  }, [])

  const value = useMemo(
    () => ({ status, user, signIn, signOut, refreshUser }),
    [status, user, signIn, signOut, refreshUser],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside a SessionProvider')
  return value
}
